// Build a new draft session, pre-filled from the deterministic engine (+ any
// accepted coach override) so logging is mostly confirming. Persists as a draft
// (not synced) until the user finishes.

import type { Exercise, GymId, Routine, Session, SetLog, ExerciseEntry, SuggestedAdjustment } from '../../domain/types';
import { buildExercisePlan, type PlannedSet } from '../../domain/planner';
import { adjustmentForGym, resolveExerciseForGym } from '../../domain/gym';
import { exerciseHistory, newId, putDraft, getMeta, setMeta } from '../../db/repo';
import { db } from '../../db/db';
import { todayISO } from '../../lib/dates';

function adjKey(exerciseId: string): string {
  return `adj:${exerciseId}`;
}

// The override that was actually applied when this exercise was last planned.
// Kept because accepting an adjustment is one-shot: it's cleared from `adj:` the
// moment it's used, so a later re-plan (correcting the gym mid-session) would
// otherwise silently drop a coach hold/deload and hand back the engine's bump —
// exactly the call that exists to protect the elbow. One entry per exercise,
// overwritten on each plan, so it doesn't accumulate.
function appliedKey(exerciseId: string): string {
  return `adjApplied:${exerciseId}`;
}

async function rememberApplied(exerciseId: string, adj: SuggestedAdjustment | null): Promise<void> {
  await setMeta(appliedKey(exerciseId), adj ? JSON.stringify(adj) : '');
}

async function getAppliedAdjustment(exerciseId: string): Promise<SuggestedAdjustment | null> {
  const raw = await getMeta(appliedKey(exerciseId));
  if (!raw) return null;
  try {
    return JSON.parse(raw) as SuggestedAdjustment;
  } catch {
    return null;
  }
}

export async function getAcceptedAdjustment(exerciseId: string): Promise<SuggestedAdjustment | null> {
  const raw = await getMeta(adjKey(exerciseId));
  if (!raw) return null;
  try {
    return JSON.parse(raw) as SuggestedAdjustment;
  } catch {
    return null;
  }
}

// Resolve which exercise an adjustment targets (id wins, else match by name) and
// store it as the override applied to that exercise's next session. For a swap,
// resolve the target exercise too; reject the apply if it can't be matched (so the
// Coach tab tells Marko instead of silently storing a swap that never fires).
// `sourceGymId` is the gym of the session the advice was written about. It's
// stamped here so that when the override is applied later, an absolute weight from
// the other gym's machine can be dropped while the injury judgement is kept.
export async function acceptAdjustment(
  adj: SuggestedAdjustment,
  sourceGymId: GymId | null,
): Promise<string | null> {
  const all = await db.exercises.toArray();
  const byName = (name: string | null | undefined) =>
    (name ? all.find((e) => e.name.toLowerCase() === name.toLowerCase())?.id ?? null : null);

  const exerciseId = adj.exerciseId ?? byName(adj.exerciseName);
  if (!exerciseId) return null;

  let swapToExerciseId = adj.swapToExerciseId ?? null;
  if (adj.kind === 'swap') {
    swapToExerciseId = swapToExerciseId ?? byName(adj.swapToExerciseName);
    if (!swapToExerciseId) return null; // can't resolve what to swap to
  }

  await setMeta(adjKey(exerciseId), JSON.stringify({ ...adj, exerciseId, swapToExerciseId, sourceGymId }));
  return exerciseId;
}

export async function clearAdjustment(exerciseId: string): Promise<void> {
  await setMeta(adjKey(exerciseId), '');
}

function plannedToSetLog(p: PlannedSet, index: number, structure: Exercise['structure']): SetLog {
  // AMRAP sets are failure by nature; everything else defaults to 1 RIR.
  const rir = p.isWarmup ? null : structure === 'amrap' ? 0 : 1;
  return {
    setNumber: index + 1,
    isWarmup: p.isWarmup,
    weightKg: p.weightKg,
    reps: p.targetReps,
    rir,
    isFailure: rir === 0,
    logged: false,
  };
}

// `accepted` is passed in (resolved + cleared by the caller) rather than read here,
// so a coach override applies exactly once to the session being built.
async function buildEntry(
  ex: Exercise,
  order: number,
  targetSets: number,
  accepted: SuggestedAdjustment | null,
  gymId: GymId | null,
): Promise<ExerciseEntry> {
  // Plan off this gym's history only, and against this gym's increment, so the
  // pre-filled load is a weight that exists on the equipment in front of him.
  const history = await exerciseHistory(ex.id, { limit: 6, gymId });
  // Drop a weight borrowed from the other gym's machine; keep the injury call.
  const forHere = adjustmentForGym(accepted, gymId);
  const plan = buildExercisePlan(resolveExerciseForGym(ex, gymId), history, targetSets, forHere);
  // Remember the ORIGINAL (weight and source gym intact) so a later re-plan at a
  // different gym can make the same call afresh rather than inheriting this one's.
  await rememberApplied(ex.id, accepted);
  const sets = plan.planned.map((p, i) => plannedToSetLog(p, i, ex.structure));
  return {
    exerciseId: ex.id,
    order,
    substitutedFrom: null,
    sets,
    elbowFeel: null,
    forearmFeel: null,
    // A coach grip bias pre-selects the grip on a grip-tracked exercise.
    gripVariant: ex.tracksGrip ? (accepted?.suggestedGrip ?? null) : null,
    note: '',
  };
}

export async function createDraftFromRoutine(
  routine: Routine,
  exById: Map<string, Exercise>,
  gymId: GymId,
): Promise<Session> {
  const entries: ExerciseEntry[] = [];
  let order = 0;
  for (const slot of routine.slots) {
    const planned = exById.get(slot.exerciseId);
    if (!planned) continue;
    const accepted = await getAcceptedAdjustment(slot.exerciseId);

    // An accepted swap replaces the slot's exercise on this session. The swapped-in
    // movement plans off its OWN history (its weights, not the original's), so only a
    // grip bias is carried over to it.
    let ex = planned;
    let substitutedFrom: string | null = null;
    let override = accepted;
    if (accepted?.kind === 'swap' && accepted.swapToExerciseId) {
      const target = exById.get(accepted.swapToExerciseId);
      if (target) {
        ex = target;
        substitutedFrom = planned.id;
        override = accepted.suggestedGrip
          ? { ...accepted, kind: 'note', suggestedWeightKg: null, suggestedRepRange: null }
          : null;
      }
    }

    const entry = await buildEntry(ex, order++, slot.targetSets, override, gymId);
    entry.substitutedFrom = substitutedFrom;
    entries.push(entry);

    // One-shot: the weekly call applies to this session, then the engine resumes
    // from the freshly logged numbers.
    if (accepted) await clearAdjustment(slot.exerciseId);
  }
  return persistDraft({
    gymId,
    routineId: routine.id,
    routineLabel: routine.dayLabel,
    entries,
  });
}

export async function createEmptyDraft(gymId: GymId): Promise<Session> {
  return persistDraft({ gymId, routineId: null, routineLabel: 'Freestyle', entries: [] });
}

// `gymId` is required, not defaulted: a silent default would quietly re-enable the
// cross-gym mixing this whole module exists to prevent, and TypeScript would not
// catch a caller that forgot it.
export async function entryForExercise(
  ex: Exercise,
  order: number,
  targetSets: number,
  gymId: GymId | null,
): Promise<ExerciseEntry> {
  const accepted = await getAcceptedAdjustment(ex.id);
  const entry = await buildEntry(ex, order, targetSets, accepted, gymId);
  if (accepted) await clearAdjustment(ex.id);
  return entry;
}

// Re-planning after a mid-session gym correction is split in two on purpose.
// planWorkingSetsForGym does the async DB work; applyPlanToEntry is pure, so the
// caller can run it INSIDE a state updater against whatever the session looks like
// once the awaits resolve. Doing it in one async step would mean writing back a
// pre-await snapshot and silently dropping any set logged in the meantime.
export async function planWorkingSetsForGym(
  ex: Exercise,
  workingCount: number,
  gymId: GymId | null,
): Promise<PlannedSet[]> {
  // Re-apply whatever override was used when this entry was first planned. A gym
  // correction must not quietly promote a coach hold back to the engine's bump.
  const applied = await getAppliedAdjustment(ex.id);
  const history = await exerciseHistory(ex.id, { limit: 6, gymId });
  const plan = buildExercisePlan(
    resolveExerciseForGym(ex, gymId), history, Math.max(1, workingCount),
    adjustmentForGym(applied, gymId),
  );
  return plan.planned;
}

// Rewrite only the not-yet-logged working sets. Set count, ordering and warm-up
// rows are Marko's, not the planner's, and an already-logged set is a real
// observation a gym correction must never overwrite.
export function applyPlanToEntry(entry: ExerciseEntry, planned: PlannedSet[]): ExerciseEntry {
  let i = 0;
  const sets = entry.sets.map((s) => {
    if (s.isWarmup) return s;
    const p = planned[i++];
    if (s.logged === false && p) return { ...s, weightKg: p.weightKg, reps: p.targetReps };
    return s;
  });
  return { ...entry, sets };
}

async function persistDraft(
  partial: Pick<Session, 'gymId' | 'routineId' | 'routineLabel' | 'entries'>,
): Promise<Session> {
  const now = new Date();
  const iso = now.toISOString();
  const session: Session = {
    id: newId('sess'),
    date: todayISO(now),
    bodyweightKg: null,
    sessionNote: '',
    createdAt: iso,
    updatedAt: iso,
    finishedAt: null,
    coaching: null,
    suggestedAdjustments: null,
    coachedAt: null,
    ...partial,
  };
  await putDraft(session);
  return session;
}
